import React from 'react';
import clsx from 'clsx';
import { getAssessmentStatusLabel } from '@utils/referenceCatalog';
import styles from './StatusChart.module.css';

interface StatusData {
  status: string;
  count: number;
}

interface StatusChartProps {
  data: StatusData[];
  isLoading: boolean;
  dataTestId?: string;
}

const StatusChart: React.FC<StatusChartProps> = ({ data, isLoading, dataTestId = 'status-chart' }) => {
  if (isLoading) {
    return (
      <div className={styles.statusChartContainer} data-test-id={`${dataTestId}-container`}>
        <div className={styles.chartHeader}>
          <h3>Assessment Status Distribution</h3>
        </div>
        <div className={clsx(styles.statusChart, styles.loading)}>
          <div className={styles.chartSkeleton}>
            {[1, 2, 3, 4].map((i) => (
              <div key={`skeleton-${i}`} className={styles.skeletonBar} style={{ height: `${30 + i * 15}%` }}></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const total = data.reduce((sum, item) => sum + item.count, 0);

  const statusColors: { [key: string]: string } = {
    Pending: '#f59e0b',
    Scheduled: '#3b82f6',
    Rescheduled: '#f97316',
    'Pending Evidence': '#8b5cf6',
    'Evidence Past Due': '#f97316',
    Escalated: '#ef4444',
    Final: '#10b981',
    Cancelled: '#6b7280',
    Expired: '#9ca3af',
    'Not Scheduled': '#6b7280'
  };

  const sortedData = [...data].sort((a, b) => b.count - a.count);

  return (
    <div className={styles.statusChartContainer} data-test-id={`${dataTestId}-container`}>
      <div className={styles.chartHeader}>
        <h3>Assessment Status Distribution</h3>
        <div className={styles.chartTotal}>{total} total assessments</div>
      </div>

      <div className={styles.statusChart}>
        <div className={styles.chartBars}>
          {sortedData.map((item, index) => {
            const percentage = total > 0 ? (item.count / total) * 100 : 0;
            const color = statusColors[item.status] || '#6b7280';

            return (
              <div key={item.status} className={styles.statusBarContainer} data-test-id={`${dataTestId}-bar`}>
                <div className={styles.statusBarInfo}>
                  <div className={styles.statusLabel}>
                    <div className={styles.statusColorDot} style={{ backgroundColor: color }}></div>
                    {getAssessmentStatusLabel(item.status)}
                  </div>
                  <div className={styles.statusCount}>
                    {item.count} ({percentage.toFixed(1)}%)
                  </div>
                </div>
                <div className={styles.statusBarTrack}>
                  <div
                    className={styles.statusBarFill}
                    style={{
                      width: `${percentage}%`,
                      backgroundColor: color
                    }}
                  ></div>
                </div>
              </div>
            );
          })}
        </div>

        {sortedData.length === 0 && (
          <div className={styles.emptyChart}>
            <p>No assessment data available</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default StatusChart;
